import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Brand } from '../components/Brand';
import { ConnectivityBanner } from '../components/ConnectivityBanner';
import { useAuth } from '../contexts/AuthContext';
import { useOfflineSync } from '../contexts/OfflineSyncContext';
import { subscribeUserNotifications } from '../repositories/collaborationRepository';
import { listenForForegroundPush } from '../repositories/notificationRepository';
import { listSpaces } from '../repositories/spaceRepository';
import type { Space } from '../types/models';
import { SpaceAvatar } from '../features/spaces/SpaceAvatar';
import { planLabel } from '../services/entitlements';
import { warmRouteV107 } from '../services/routeWarmupV107';
import { ThemeStudioV2Runtime } from '../components/ThemeStudioV2Runtime';
import { SidebarCustomizer } from '../components/SidebarCustomizer';
import {
  PERSONALISATION_EVENT,
  applyPersonalisation,
  defaultPersonalisation,
  loadPersonalisation,
  navigationIcon,
  orderedNavigation,
  savePersonalisation,
  type PersonalisationSettings,
} from '../services/personalisation';

interface ActivityToast {
  id: string;
  title: string;
  summary: string;
  targetPath: string;
}

function NotificationBellIcon() {
  return (
    <svg
      className="notification-bell-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </svg>
  );
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [businessSpaces, setBusinessSpaces] = useState<Space[]>([]);
  const [businessPickerOpen, setBusinessPickerOpen] = useState(false);
  const [businessPickerLoading, setBusinessPickerLoading] = useState(false);
  const [businessPickerError, setBusinessPickerError] = useState('');
  const [personalisation, setPersonalisation] = useState<PersonalisationSettings>(defaultPersonalisation());
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [activityToast, setActivityToast] = useState<ActivityToast | null>(null);
  const { profile, user, logOut } = useAuth();
  const { pendingCount, needsAttentionCount, syncing } = useOfflineSync();
  const navigate = useNavigate();
  const location = useLocation();

  // Phase 10.7: warm only route JS and short-lived owner-scoped Goals data
  // when the user expresses navigation intent. No account/balance persistence.
  useEffect(() => {
    if (!user?.uid) return;
    const ownerId = user.uid;
    const onIntent = (event: Event) => {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) {
        if (event.target.closest('button.mobile-bottom-add')) warmRouteV107('/transactions');
        return;
      }
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      warmRouteV107(url.pathname);
      if (url.pathname === '/goals') {
        void import('../services/goalsWarmupV107')
          .then(({ warmGoalsV107 }) => warmGoalsV107(ownerId))
          .catch(() => undefined);
      }
    };
    document.addEventListener('pointerover', onIntent, { passive: true });
    document.addEventListener('pointerdown', onIntent, { passive: true });
    document.addEventListener('focusin', onIntent);
    // Low-priority warming is intentionally limited to two small, common pages.
    const idle = window.setTimeout(() => {
      if (document.visibilityState === 'hidden' || window.location.pathname !== '/') return;
      warmRouteV107('/goals');
      warmRouteV107('/more');
    }, 1600);
    return () => {
      window.clearTimeout(idle);
      document.removeEventListener('pointerover', onIntent);
      document.removeEventListener('pointerdown', onIntent);
      document.removeEventListener('focusin', onIntent);
    };
  }, [user?.uid]);


  const visibleNavigation = useMemo(
    () => orderedNavigation(personalisation),
    [personalisation],
  );
  const currentPlanLabel = planLabel(profile);

  useEffect(() => {
    if (!user) {
      const defaults = defaultPersonalisation();
      setPersonalisation(defaults);
      applyPersonalisation(defaults);
      return;
    }

    const initial = loadPersonalisation(user.uid);
    setPersonalisation(initial);
    applyPersonalisation(initial);

    const handlePersonalisation = (event: Event) => {
      const detail = (event as CustomEvent<{ uid: string; settings: PersonalisationSettings }>).detail;
      if (!detail || detail.uid !== user.uid) return;
      setPersonalisation(detail.settings);
      applyPersonalisation(detail.settings);
    };
    window.addEventListener(PERSONALISATION_EVENT, handlePersonalisation);
    return () => window.removeEventListener(PERSONALISATION_EVENT, handlePersonalisation);
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/search') setSearchText(new URLSearchParams(location.search).get('q') || '');
  }, [location]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!user) {
      setUnreadNotifications(0);
      setActivityToast(null);
      return;
    }

    let initialized = false;
    const known = new Set<string>();

    return subscribeUserNotifications(
      user.uid,
      (items) => {
        setUnreadNotifications(
          items.filter((item) => !item.readAt).length,
        );

        if (!initialized) {
          items.forEach((item) => known.add(item.id));
          initialized = true;
          return;
        }

        const fresh = items.filter((item) => !known.has(item.id));
        items.forEach((item) => known.add(item.id));

        const newestActivity = fresh.find(
          (item) => item.source === 'activity',
        );

        if (!newestActivity) return;

        setActivityToast({
          id: newestActivity.id,
          title: newestActivity.title || 'Space update',
          summary: newestActivity.message || 'A Space was updated.',
          targetPath: newestActivity.targetPath || '/notifications',
        });
      },
      () => setUnreadNotifications(0),
    );
  }, [user]);

  useEffect(() => {
    if (!activityToast) return;
    const timer = window.setTimeout(() => setActivityToast(null), 8000);
    return () => window.clearTimeout(timer);
  }, [activityToast]);

  useEffect(() => {
    if (!profile?.browserPushEnabled || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    let active = true;
    let stop: () => void = () => {};
    void listenForForegroundPush((payload) => {
      if (!active) return;
      const title = payload.data?.title || payload.notification?.title || 'BajetBN reminder';
      const body = payload.data?.body || payload.notification?.body || 'Open BajetBN to see the update.';
      const targetPath = payload.data?.targetPath || '/notifications';
      const notification = new Notification(title, { body, icon: '/icons/bajetbn-192.png', tag: payload.data?.notificationId || undefined });
      notification.onclick = () => {
        window.focus();
        navigate(targetPath);
        notification.close();
      };
    }).then((unsubscribe) => { if (active) stop = unsubscribe; else unsubscribe(); });
    return () => { active = false; stop(); };
  }, [navigate, profile?.browserPushEnabled]);

  function updateSidebarPersonalisation(
    next: PersonalisationSettings,
  ) {
    if (!user) return;

    const saved =
      savePersonalisation(
        user.uid,
        next,
      );

    setPersonalisation(saved);
  }

  async function openBusinessShortcut() {
    if (!user || businessPickerLoading) return;

    setBusinessPickerLoading(true);
    setBusinessPickerError('');
    setBusinessPickerOpen(false);

    try {
      const accessibleSpaces = await listSpaces(user.uid);
      const smeSpaces = accessibleSpaces.filter(
        (space) => space.type === 'sme' && !space.archivedAt,
      );

      setBusinessSpaces(smeSpaces);

      if (smeSpaces.length === 0) {
        navigate('/spaces');
        return;
      }

      if (smeSpaces.length === 1) {
        navigate(`/business/${smeSpaces[0].id}`);
        return;
      }

      setBusinessPickerOpen(true);
    } catch {
      setBusinessSpaces([]);
      setBusinessPickerError(
        'Your Business Spaces could not be loaded. Open Spaces and try again.',
      );
      setBusinessPickerOpen(true);
    } finally {
      setBusinessPickerLoading(false);
    }
  }

  function chooseBusinessSpace(space: Space) {
    setBusinessPickerOpen(false);
    setBusinessPickerError('');
    navigate(`/business/${space.id}`);
  }
  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const query = searchText.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
  }

  return (
    <div className={`app-shell ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeStudioV2Runtime />
      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-top">
          <Brand compact={collapsed} />
          <button className="icon-button desktop-only" onClick={() => setCollapsed((value) => !value)} aria-label="Toggle sidebar">⇤</button>
          <button className="icon-button mobile-only" onClick={() => setMobileOpen(false)} aria-label="Close menu">×</button>
        </div>
        <nav>
          {visibleNavigation.map((item) => (
            <NavLink key={item.path} to={item.path} end={item.path === '/'} onClick={() => setMobileOpen(false)}>
              <span className="nav-icon">{navigationIcon(personalisation.iconPack, item.id, item.icon)}</span>
              <span className="nav-label">{item.label}</span>
              {item.path === '/offline-sync' && (pendingCount + needsAttentionCount > 0 || syncing) && <span className={`nav-count ${needsAttentionCount > 0 ? 'attention' : ''}`}>{syncing ? '…' : pendingCount + needsAttentionCount}</span>}
            </NavLink>
          ))}

        </nav>
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-user desktop-only"
            onClick={() => setCustomizerOpen(true)}
            title="Customize menu"
          >
            <span className="nav-icon">☷</span>
            <span className="nav-label">
              <strong>Customize menu</strong>
              <small>Order and pin sidebar tools</small>
            </span>
          </button>

          <NavLink
            to="/subscription"
            className="sidebar-plan-link"
            onClick={() => setMobileOpen(false)}
          >
            <span className="nav-icon">
              {currentPlanLabel === 'Plus' ? '✦' : '○'}
            </span>
            <span className="nav-label">
              <strong>BajetBN {currentPlanLabel}</strong>
              <small>
                {currentPlanLabel === 'Plus'
                  ? 'Subscription active'
                  : 'Free plan · View Plus'}
              </small>
            </span>
          </NavLink>

          <NavLink to="/settings" onClick={() => setMobileOpen(false)}>
            <span className="nav-icon">⚙</span>
            <span className="nav-label">Settings</span>
          </NavLink>
          <button type="button" className="sidebar-user" onClick={() => void logOut()} title="Sign out">
            <span className="avatar">{(profile?.fullName || user?.email || 'B').charAt(0).toUpperCase()}</span>
            <span className="nav-label">
              <strong>{profile?.fullName || 'BajetBN user'}</strong>
              <small>Sign out</small>
            </span>
          </button>
        </div>
      </aside>

      {customizerOpen && (
        <SidebarCustomizer
          settings={personalisation}
          onChange={updateSidebarPersonalisation}
          onClose={() => setCustomizerOpen(false)}
        />
      )}

      {mobileOpen && <button className="drawer-backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
      <div className="app-main">
        <header className="mobile-header">

          <Brand compact />
          <div className="mobile-header-actions">
            <button
              className="icon-button"
              onClick={() => navigate('/search')}
              aria-label="Search"
            >
              ⌕
            </button>

            <button
              className="icon-button notification-button"
              onClick={() => navigate('/notifications')}
              aria-label={`${unreadNotifications} unread notifications`}
            >
              <NotificationBellIcon />

              {unreadNotifications > 0 && (
                <span className="notification-count">
                  {unreadNotifications > 99
                    ? '99+'
                    : unreadNotifications}
                </span>
              )}
            </button>
          </div>
        </header>
        <div className="desktop-environment">
          <form className="top-search-form" onSubmit={submitSearch}><span>⌕</span><input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search BajetBN" aria-label="Search BajetBN" /></form>
          <button className="icon-button notification-button" onClick={() => navigate('/notifications')} aria-label={`${unreadNotifications} unread notifications`}><NotificationBellIcon />{unreadNotifications > 0 && <span className="notification-count">{unreadNotifications > 99 ? '99+' : unreadNotifications}</span>}</button>
          <span className="environment-badge">{import.meta.env.VITE_APP_ENV || 'local'}</span>
        </div>
        <ConnectivityBanner />
        {activityToast && (
          <button
            type="button"
            className="space-activity-live-toast"
            onClick={() => {
              const target = activityToast.targetPath;
              setActivityToast(null);
              navigate(target);
            }}
          >
            <span className="space-activity-live-toast-icon">●</span>
            <span className="space-activity-live-toast-copy">
              <strong>{activityToast.title}</strong>
              <span>{activityToast.summary}</span>
              <small>Just updated · Tap to view details</small>
            </span>
            <span className="space-activity-live-toast-arrow" aria-hidden="true">→</span>
          </button>
        )}
        <Outlet />

        <nav className="mobile-bottom-nav" aria-label="Quick navigation">
          <NavLink
            to="/"
            end
            className={({ isActive }) => isActive ? 'active' : ''}
          >
            <span className="bajetbn-v105-bottom-icon" aria-hidden="true"><img src="/category-icons-v102/home.svg" alt="" decoding="async" /></span>
            <small>Home</small>
          </NavLink>

          <NavLink
            to="/transactions"
            className={({ isActive }) => isActive ? 'active' : ''}
          >
            <span className="bajetbn-v105-bottom-icon" aria-hidden="true"><img src="/navigation-icons-v104/activity.svg" alt="" decoding="async" /></span>
            <small>Transactions</small>
          </NavLink>

          <button
            type="button"
            className="mobile-bottom-add"
            onClick={() => navigate('/?quick=1')}
            aria-label="Add income or expense"
          >
            <span className="bajetbn-v105-bottom-icon" aria-hidden="true"><img src="/navigation-icons-v104/add.svg" alt="" decoding="async" /></span>
            <small>Add</small>
          </button>

          <NavLink
            to="/goals"
            className={({ isActive }) => isActive ? 'active' : ''}
          >
            <span className="bajetbn-v105-bottom-icon" aria-hidden="true"><img src="/category-icons-v102/target.svg" alt="" decoding="async" /></span>
            <small>Goals</small>
          </NavLink>

          <NavLink
            to="/more"
            className={({ isActive }) => isActive ? 'active' : ''}
          >
            <span className="bajetbn-v105-bottom-icon" aria-hidden="true"><img src="/navigation-icons-v105/more.svg" alt="" decoding="async" /></span>
            <small>More</small>
          </NavLink>
        </nav>

        {businessPickerOpen && (
          <>
            <button
              type="button"
              className="mobile-business-picker-backdrop"
              aria-label="Close business menu"
              onClick={() => {
                setBusinessPickerOpen(false);
                setBusinessPickerError('');
              }}
            />

            <section
              className="mobile-business-picker"
              aria-label="Choose a Business"
            >
              <header className="mobile-business-picker-header">
                <div>
                  <small>Business</small>
                  <strong>
                    {businessPickerError
                      ? 'Business unavailable'
                      : 'Choose a Business'}
                  </strong>
                </div>

                <button
                  type="button"
                  aria-label="Close business menu"
                  onClick={() => {
                    setBusinessPickerOpen(false);
                    setBusinessPickerError('');
                  }}
                >
                  ×
                </button>
              </header>

              {businessPickerError ? (
                <div className="notice error">
                  {businessPickerError}
                </div>
              ) : (
                <div className="mobile-business-picker-list">
                  {businessSpaces.map((space) => (
                    <button
                      type="button"
                      key={space.id}
                      className="mobile-business-picker-option"
                      onClick={() => chooseBusinessSpace(space)}
                    >
                      <SpaceAvatar
                        space={space}
                      />

                      <span>
                        <strong>{space.name}</strong>
                        <small>
                          Business Space · {space.currency}
                        </small>
                      </span>

                      <b aria-hidden="true">›</b>
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="mobile-business-picker-all"
                onClick={() => {
                  setBusinessPickerOpen(false);
                  setBusinessPickerError('');
                  navigate('/spaces');
                }}
              >
                View all Spaces
                <span aria-hidden="true">›</span>
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
